import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { offerApi } from '@texas/api/endpoints/offerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  offeredOfferId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export function OfferedOfferDetailsDrawer({
  offeredOfferId,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{t('offer.offerOfferedOffer')}</DrawerHeader>
        {isOpen && offeredOfferId && (
          <OfferedOfferDetailsDrawerInner offeredOfferId={offeredOfferId} />
        )}
      </DrawerContent>
    </Drawer>
  );
}

function OfferedOfferDetailsDrawerInner({
  offeredOfferId,
}: {
  offeredOfferId: number;
}) {
  const { t } = useTranslation();
  const {
    data: offeredOffer,
    loading,
    error,
    refetch,
  } = useApiResource(offerApi.getOfferedOffer);

  useEffect(() => {
    refetch(offeredOfferId);
  }, [refetch, offeredOfferId]);

  return (
    <DrawerBody>
      {loading && <Spinner size="sm" />}
      {error && <ErrorDetails error={error} />}
      {offeredOffer && (
        <Flex direction="column" gap={4}>
          <Box>
            <Text fontWeight="semibold">{offeredOffer.offerNumber}</Text>
            <Text fontSize="sm" color="gray.400">
              {offeredOffer.customerName}
            </Text>
            <Text fontSize="xs" color="gray.400">
              {t('offer.sent')} {formatDate(Locale.En, offeredOffer.sentOn)}
              {' · '}
              {t('offer.expires')}{' '}
              {formatDate(Locale.En, offeredOffer.expiresOn)}
              {' · '}
              {offeredOffer.ownerName}
            </Text>
          </Box>
          {offeredOffer.branches.map((branch) => (
            <Flex key={branch.branchId} direction="column" gap={2}>
              <Flex align="center" gap={2}>
                <Text fontWeight="semibold" fontSize="sm">
                  {branch.branchName}
                </Text>
                <Badge>{branch.branchIdentifier}</Badge>
              </Flex>
              {branch.currencies.map((currency) => {
                const showMatrixColumn = currency.tierPrices.some(
                  (tierPrice) =>
                    tierPrice.matrixNodeValueXName != null ||
                    tierPrice.matrixNodeValueYName != null,
                );

                return (
                  <TableContainer key={currency.currencyId}>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          {showMatrixColumn && <Th>{currency.currencyCode}</Th>}
                          <Th>{t('general.quantity')}</Th>
                          <Th>{t('inquiry.price')}</Th>
                          <Th>{t('general.status')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {currency.tierPrices.map((tierPrice) => (
                          <Tr key={tierPrice.id}>
                            {showMatrixColumn && (
                              <Td>
                                <Flex gap={1} wrap="wrap">
                                  {tierPrice.matrixNodeValueYName && (
                                    <Tag size="sm">
                                      {tierPrice.matrixNodeValueYName}
                                    </Tag>
                                  )}
                                  {tierPrice.matrixNodeValueXName && (
                                    <Tag size="sm">
                                      {tierPrice.matrixNodeValueXName}
                                    </Tag>
                                  )}
                                </Flex>
                              </Td>
                            )}
                            <Td>{tierPrice.quantity}</Td>
                            <Td>
                              {tierPrice.price != null
                                ? `${tierPrice.price} ${currency.currencyCode}`
                                : '—'}
                            </Td>
                            <Td>
                              {tierPrice.acceptedDate ? (
                                <Badge colorScheme="green">
                                  {t('general.accepted')}
                                </Badge>
                              ) : (
                                <Badge colorScheme="gray">
                                  {t('offer.pending')}
                                </Badge>
                              )}
                              {tierPrice.acceptedByUserName && (
                                <Text fontSize="2xs" color="gray.400">
                                  {tierPrice.acceptedByUserName}
                                </Text>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                );
              })}
            </Flex>
          ))}
        </Flex>
      )}
    </DrawerBody>
  );
}
